import axios from "axios";

import { Store } from "../store";
import { b64toutf8 } from "../encoding";
import { Configuration } from "../configuration";
import { setLocale } from "../i18n";

import "./directives";
import Axios from "axios";

export interface IdpConfiguration {
  clientId: string;
  claims: { [key: string]: any } | null;
}

const refreshTokenInProcess: any[] = [];
export class Auth {
  private static config: IdpConfiguration;
  /**
   * public constant used for more readability - return always true
   *
   */
  public static get calledOnError401() {
    return true;
  }
  /** The claims of the authenticated user or (if anonymous) null. */
  public static claims: { [key: string]: any } | null;

  private static refreshTokenTime: number | null;
  /**
   * Ensures the user is authenticated & claims are up-to-date.
   * If claims are not present and/or could not be refreshed,
   * the browser is redirected to the IDP login page.
   */
  public static async ensureAuthenticated(calledOnError401 = false) {
    this.config = this.config || (await Configuration.get<IdpConfiguration>("idp"));
    if (this.claims) {
      if (calledOnError401) {
        if (this.refreshTokenTime && Date.now() - this.refreshTokenTime < THIRTY_SECONDS) {
          return this.claims;
        }
      } else {
        if (!(await this.getClaim("exp")) || new Date((await this.getClaim("exp")) * 1000 - TEN_SECONDS) > new Date()) {
          return this.claims;
        }
      }
      if (await this.refreshToken()) {
        return this.claims;
      }
    }

    this.redirectToIdp();
  }

  public static async Force401RefreshToken() {
    this.redirectToIdp();
  }

  /** Verifies wether the current user is in at least one of the specified roles. */
  public static isInRole(...roles: string[]): boolean {
    if (!roles.length) {
      return true;
    }
    return !!this.claims?.resource_access[this.config.clientId]?.roles?.find((r: string) => roles.includes(r));
  }

  /** Clears all claims and redicts to the IDP log-out URL. */
  public static logOut(uriParams?: string) {
    this.initClaims(null);
    window.location.href = `${process.env.BASE_URL}oauth2/log-out?redirect_uri=` +
    encodeURIComponent(`${window.location.protocol}//${window.location.host}${process.env.BASE_URL}logged-off${uriParams ? "?" + uriParams : ""}`);
  }

  public static async initClaims(jwtPayload: string | null, justAuthenticated?: boolean | null) {
    if (justAuthenticated) {
      jwtPayload = btoa(JSON.stringify((await Axios.get<any>(`${process.env.BASE_URL}oauth2/get-jwt-payload`)).data));
    }
    this.claims = jwtPayload ? JSON.parse(b64toutf8(jwtPayload)) : null;
    Store.set("jwtPayload", jwtPayload);
    if (this.claims) {
      setTimeout(() => {
        setLocale(this.claims!["PrefLanguage"], true);
      });
    }
  }

  public static async getClaim(key: string): Promise<any> {
    this.config = this.config || (await Configuration.get<IdpConfiguration>("idp"));
    return (
      (this.config?.claims && this.claims && this.claims[this.config?.claims[key]]) ||
      (this.claims && this.claims[key]) ||
      undefined
    );
  }

  public static redirectToIdp(sessionId?: string) {
    window.location.href = `${process.env.BASE_URL}oauth2/redirect${sessionId ? "?sessionId=" + sessionId : ""}`;
  }

  private static refreshToken(): Promise<boolean> {
    const result = new Promise<boolean>(resolve => {
      refreshTokenInProcess.push(resolve);
    });
    if (refreshTokenInProcess.length == 1) {
      this.getRefreshToken().then(res => {
        while (refreshTokenInProcess.length > 0) {
          const resolve = refreshTokenInProcess.shift();
          resolve(res);
        }
      });
    }
    return result;
  }

  private static async getRefreshToken() {
    try {
      const res = (await axios.get(`${process.env.BASE_URL}oauth2/refresh`)).data;
      this.initClaims(res.jwtPayload);
      this.refreshTokenTime = Date.now();
      return true;
    } catch {
      return false;
    }
  }
}

const urlParams = new URLSearchParams(window.location.search);

Auth.initClaims(urlParams.get("jwtPayload") || (Store.get("jwtPayload") as string | null), urlParams.get("justAuthenticated") === "true");

const TEN_SECONDS = 10_000;
const THIRTY_SECONDS = 30_000;
